/**
 * Batch rows of `/settings/delivery-flow` (#2454): the board range and the
 * delivery calendar (the weekly rest days).
 *
 * The shown value is the EFFECTIVE one from `GET /api/delivery-flow` (the
 * backend has already fallen back to its default); the badge and the
 * "Reset to default" button read `GET /api/tenant/settings`. The front end
 * holds no default of its own.
 *
 * - Board range: three buttons, the current one `aria-pressed`; a click saves.
 * - Rest days: seven toggles (ISO weekday 1 = Monday … 7 = Sunday); a change
 *   shows "Save" / "Cancel". Clearing every day saves `[]`.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@resource-kit/ui/react';
import type { TenantSettings, TenantSettingsBody } from '@/services/tenants';
import { requestErrorMessage, useSaveTenantSettings } from '../hooks/useTenants';
import { SettingsRow } from './SettingsSection';
import { type DeliveryFlow, SourceBadge, settingsBody } from './deliveryFlowShared';

/** The closed set `PUT /api/tenant/settings` accepts (#2449). */
const BOARD_RANGE_OPTIONS = [7, 10, 14] as const satisfies readonly NonNullable<
  TenantSettingsBody['boardRangeDays']
>[];

const WEEKDAY_LABEL_KEY = {
  1: 'deliveryFlow.weekday.1',
  2: 'deliveryFlow.weekday.2',
  3: 'deliveryFlow.weekday.3',
  4: 'deliveryFlow.weekday.4',
  5: 'deliveryFlow.weekday.5',
  6: 'deliveryFlow.weekday.6',
  7: 'deliveryFlow.weekday.7',
} as const;

const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;

function SaveError({ error, id }: { error: unknown; id: string }) {
  const { t } = useTranslation();
  return (
    <p id={id} role="alert" className="text-sm text-destructive">
      {t('tenants.settings.saveFailed', { message: requestErrorMessage(error) })}
    </p>
  );
}

export function BoardRangeRow({
  flow,
  settings,
}: {
  flow: DeliveryFlow;
  settings: TenantSettings;
}) {
  const { t } = useTranslation();
  const mutation = useSaveTenantSettings();
  const save = (boardRangeDays: TenantSettingsBody['boardRangeDays']) =>
    mutation.mutate(settingsBody(settings, { boardRangeDays }));

  return (
    <SettingsRow
      testId="delivery-flow-row-boardRangeDays"
      label={t('deliveryFlow.row.boardRange')}
      action={
        settings.boardRangeDays.source === 'tenant' && (
          <Button
            variant="ghost"
            size="sm"
            disabled={mutation.isPending}
            onClick={() => save(null)}
          >
            {t('tenants.settings.resetToDefault')}
          </Button>
        )
      }
    >
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          {BOARD_RANGE_OPTIONS.map((days) => (
            <Button
              key={days}
              size="sm"
              variant={flow.board_range_days === days ? 'default' : 'outline'}
              aria-pressed={flow.board_range_days === days}
              disabled={mutation.isPending}
              onClick={() => save(days)}
            >
              {t('deliveryFlow.range.days', { count: days })}
            </Button>
          ))}
          <SourceBadge source={settings.boardRangeDays.source} fallback="default" />
        </div>
        {mutation.isError && (
          <SaveError error={mutation.error} id="delivery-flow-board-range-error" />
        )}
      </div>
    </SettingsRow>
  );
}

export function RestWeekdaysRow({
  flow,
  settings,
}: {
  flow: DeliveryFlow;
  settings: TenantSettings;
}) {
  const { t } = useTranslation();
  const mutation = useSaveTenantSettings();
  /** The days being edited; `null` while the row shows the saved calendar. */
  const [draft, setDraft] = useState<number[] | null>(null);
  const [invalid, setInvalid] = useState(false);
  const selected = draft ?? flow.rest_weekdays;
  const errorId = invalid
    ? 'delivery-flow-calendar-error'
    : mutation.isError
      ? 'delivery-flow-calendar-save-error'
      : undefined;
  const calendarError = invalid ? (
    <p id="delivery-flow-calendar-error" role="alert" className="text-sm text-destructive">
      {t('deliveryFlow.calendar.invalid')}
    </p>
  ) : (
    mutation.isError && <SaveError error={mutation.error} id="delivery-flow-calendar-save-error" />
  );

  const toggle = (day: number) => {
    mutation.reset();
    setInvalid(false);
    setDraft(
      selected.includes(day)
        ? selected.filter((d) => d !== day)
        : [...selected, day].sort((a, b) => a - b),
    );
  };
  const cancel = () => {
    setDraft(null);
    setInvalid(false);
    mutation.reset();
  };
  const save = (restWeekdays: number[] | null) => {
    // Seven rest days would leave no delivery day; the backend refuses it too.
    if (restWeekdays !== null && restWeekdays.length === WEEKDAYS.length) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    mutation.mutate(settingsBody(settings, { restWeekdays }), {
      onSuccess: () => setDraft(null),
    });
  };

  return (
    <SettingsRow
      testId="delivery-flow-row-restWeekdays"
      label={t('deliveryFlow.row.calendar')}
      action={
        draft !== null ? (
          <>
            <Button variant="outline" size="sm" disabled={mutation.isPending} onClick={cancel}>
              {t('tenants.settings.cancel')}
            </Button>
            <Button size="sm" disabled={mutation.isPending} onClick={() => save(draft)}>
              {mutation.isPending ? t('tenants.settings.saving') : t('tenants.settings.save')}
            </Button>
          </>
        ) : (
          settings.restWeekdays.source === 'tenant' && (
            <Button
              variant="ghost"
              size="sm"
              disabled={mutation.isPending}
              onClick={() => save(null)}
            >
              {t('tenants.settings.resetToDefault')}
            </Button>
          )
        )
      }
    >
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-1">
          {WEEKDAYS.map((day) => (
            <Button
              key={day}
              size="sm"
              className="w-11"
              variant={selected.includes(day) ? 'default' : 'outline'}
              aria-pressed={selected.includes(day)}
              aria-invalid={invalid || mutation.isError || undefined}
              aria-describedby={errorId}
              disabled={mutation.isPending}
              onClick={() => toggle(day)}
            >
              {t(WEEKDAY_LABEL_KEY[day])}
            </Button>
          ))}
          <span className="ml-1">
            <SourceBadge source={settings.restWeekdays.source} fallback="default" />
          </span>
        </div>
        <p className="text-xs text-muted-foreground">{t('deliveryFlow.calendar.note')}</p>
        {calendarError}
      </div>
    </SettingsRow>
  );
}
